export const toCoords = (point: any): [number, number] | null => {
    if (Array.isArray(point) && point.length >= 2
        && typeof point[0] === 'number' && typeof point[1] === 'number') {
        return [point[0], point[1]];
    }
    if (point && typeof point.lng === 'number' && typeof point.lat === 'number') {
        return [point.lng, point.lat];
    }
    return null;
};

const unwrapGeoJSON = (obj: any): any[] => {
    if (obj?.type === 'Polygon' && Array.isArray(obj.coordinates)) {
        return obj.coordinates[0] || [];
    }
    return [];
};

const flattenPolygon = (polygon: any): any[] => {
    if (!polygon) return [];

    // GeoJSON geometry object: { type: "Polygon", coordinates: [[[lng, lat], ...]] }
    if (polygon.type === 'Polygon') {
        return unwrapGeoJSON(polygon);
    }

    if (!Array.isArray(polygon) || polygon.length === 0) return [];

    // Array que envuelve un GeoJSON: [{ type: "Polygon", coordinates: [...] }]
    if (polygon[0]?.type === 'Polygon') {
        return unwrapGeoJSON(polygon[0]);
    }

    // Si es [[[lng, lat], ...]] (GeoJSON ring), desanidar un nivel
    if (Array.isArray(polygon[0]) && Array.isArray(polygon[0][0])) {
        return polygon[0];
    }

    return polygon;
};

export const getAllCoveragesBounds = (
    coverages: { polygon: any }[],
): [[number, number], [number, number]] | null => {
    if (!coverages.length) return null;

    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    let hasPoints = false;

    coverages.forEach((c) => {
        const points = flattenPolygon(c.polygon);
        points.forEach((p: any) => {
            const coord = toCoords(p);
            if (!coord) return;
            const [lng, lat] = coord;
            if (lng < minLng) minLng = lng;
            if (lat < minLat) minLat = lat;
            if (lng > maxLng) maxLng = lng;
            if (lat > maxLat) maxLat = lat;
            hasPoints = true;
        });
    });

    if (!hasPoints) return null;
    return [[minLng, minLat], [maxLng, maxLat]];
};

export const normalizePolygon = (polygon: any): number[][] => {
    const points = flattenPolygon(polygon);
    if (!points.length) return [];

    const coords: number[][] = [];
    points.forEach((p: any) => {
        const coord = toCoords(p);
        if (coord) coords.push(coord);
    });

    if (coords.length >= 3) {
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            coords.push([first[0], first[1]]);
        }
    }

    return coords;
};

export const getPolygonCenter = (polygon: any): { lng: number; lat: number } => {
    const defaultCenter = { lng: -78.4678, lat: -0.1807 }; // Quito

    const points = flattenPolygon(polygon);
    if (!points.length) return defaultCenter;

    let lngSum = 0;
    let latSum = 0;
    let count = 0;

    points.forEach((p: any) => {
        const coord = toCoords(p);
        if (!coord) return;
        lngSum += coord[0];
        latSum += coord[1];
        count += 1;
    });

    if (count === 0) return defaultCenter;

    return {
        lng: lngSum / count,
        lat: latSum / count,
    };
};
